import { useEffect, useRef } from "react";
import styled, { createGlobalStyle, keyframes } from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const GlobalStyle = createGlobalStyle`
  @import url('https://fonts.googleapis.com/css2?family=Bebas+Neue&family=Italiana&family=DM+Sans:wght@300;400&display=swap');
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html { scroll-behavior: auto; }
  body { overflow-x: hidden; }
`;

const blink = keyframes`
  0%, 100% { opacity: 1; }
  50%       { opacity: 0; }
`;

const Wrapper = styled.div``;

const Beat = styled.div`
  position: relative;
  height: ${({ h }) => h || "220vh"};
`;

const BeatSticky = styled.div`
  position: sticky;
  top: 0;
  height: 100vh;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ bg }) => bg || "#060008"};
`;

const Grain = styled.div`
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 0;
  opacity: 0.04;
  background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E");
`;

const GiantWord = styled.div`
  font-family: "Bebas Neue", sans-serif;
  font-size: ${({ size }) => size || "clamp(5rem, 18vw, 18rem)"};
  line-height: 0.88;
  letter-spacing: ${({ ls }) => ls || "0.04em"};
  color: ${({ c }) => c || "#ffffff"};
  position: relative;
  z-index: 2;
  will-change: transform, opacity, filter;
  white-space: nowrap;

  &.stroke {
    -webkit-text-stroke: 2px rgba(255, 255, 255, 0.35);
    color: transparent;
  }
  &.gradient {
    background: linear-gradient(
      135deg,
      #e879f9 0%,
      #a855f7 40%,
      #7c3aed 70%,
      #c026d3 100%
    );
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
  }
`;

const SplitLayout = styled.div`
  display: flex;
  flex-direction: column;
  align-items: ${({ align }) => align || "flex-start"};
  gap: 0.15rem;
  position: relative;
  z-index: 2;
  width: min(1200px, 90vw);
`;

const Interlude = styled.div`
  font-family: "Italiana", serif;
  font-size: clamp(2rem, 4.5vw, 5rem);
  line-height: 1.25;
  color: rgba(255, 255, 255, 0.75);
  position: relative;
  z-index: 2;
  text-align: center;
  max-width: min(860px, 85vw);
  will-change: transform, opacity;
`;

const SmallLabel = styled.div`
  font-family: "DM Sans", sans-serif;
  font-size: clamp(0.85rem, 1.2vw, 1.1rem);
  font-weight: 400;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: ${({ c }) => c || "#a855f7"};
  opacity: 0.9;
  position: relative;
  z-index: 2;
  will-change: opacity, transform;
`;

const AccentCircle = styled.div`
  position: absolute;
  border-radius: 50%;
  background: ${({ c }) => c};
  width: ${({ s }) => s};
  height: ${({ s }) => s};
  top: ${({ top }) => top};
  left: ${({ left }) => left};
  right: ${({ right }) => right};
  filter: blur(130px);
  opacity: 0;
  z-index: 1;
  will-change: opacity, transform;
`;

const AccentLine = styled.div`
  position: absolute;
  background: linear-gradient(
    90deg,
    transparent,
    ${({ c }) => c || "rgba(168,85,247,0.55)"},
    transparent
  );
  height: 1px;
  width: 100%;
  top: ${({ top }) => top};
  left: 0;
  z-index: 1;
  transform: scaleX(0);
  transform-origin: ${({ orig }) => orig || "left"};
  will-change: transform;
`;

const Counter = styled.div`
  position: absolute;
  bottom: 2.5rem;
  right: 3rem;
  z-index: 10;
  display: flex;
  align-items: baseline;
  gap: 4px;
  font-family: "DM Sans", sans-serif;
`;

const CountNum = styled.span`
  font-size: 0.68rem;
  color: rgba(255, 255, 255, 0.5);
`;

const CountSep = styled.span`
  font-size: 0.6rem;
  color: rgba(255, 255, 255, 0.18);
`;

const Cursor = styled.span`
  display: inline-block;
  width: 4px;
  height: 0.75em;
  background: #e879f9;
  margin-left: 8px;
  vertical-align: middle;
  animation: ${blink} 0.9s step-end infinite;
`;

export default function ManifestoSection() {
  const b1Word = useRef(null);
  const b1Label = useRef(null);
  const b1Circle = useRef(null);

  const b2top = useRef(null);
  const b2bot = useRef(null);
  const b2line = useRef(null);

  const b3word = useRef(null);
  const b3sub = useRef(null);
  const b3circle = useRef(null);

  const b4top = useRef(null);
  const b4mid = useRef(null);
  const b4bot = useRef(null);

  const b5text = useRef(null);
  const b5line1 = useRef(null);
  const b5line2 = useRef(null);

  const b6word = useRef(null);
  const b6label = useRef(null);
  const b6circle = useRef(null);

  const b7line1 = useRef(null);
  const b7line2 = useRef(null);
  const b7label = useRef(null);

  useEffect(() => {
    const mm = gsap.matchMedia();

    mm.add("(min-width: 1px)", () => {
      // BEAT 1 — "FORGET" slams down from above, skewed
      gsap.set(b1Word.current, { y: "-130%", opacity: 0, skewY: -6 });
      gsap.set(b1Label.current, { opacity: 0, y: 18 });
      gsap.set(b1Circle.current, { opacity: 0, scale: 0.4 });
      gsap
        .timeline({
          scrollTrigger: {
            trigger: "#beat1",
            start: "top top",
            end: "+=80%",
            scrub: 0.6,
          },
        })
        .to(
          b1Word.current,
          { y: "0%", opacity: 1, skewY: 0, ease: "power4.out" },
          0,
        )
        .to(b1Circle.current, { opacity: 1, scale: 1, ease: "power2.out" }, 0)
        .to(b1Label.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.3);

      // BEAT 2 — two words crash in from opposite sides
      gsap.set(b2top.current, { x: "-100vw", opacity: 0 });
      gsap.set(b2bot.current, { x: "100vw", opacity: 0 });
      gsap.set(b2line.current, { scaleX: 0 });
      gsap
        .timeline({
          scrollTrigger: {
            trigger: "#beat2",
            start: "top top",
            end: "+=80%",
            scrub: 0.65,
          },
        })
        .to(b2top.current, { x: 0, opacity: 1, ease: "power3.out" }, 0)
        .to(b2bot.current, { x: 0, opacity: 1, ease: "power3.out" }, 0.06)
        .to(b2line.current, { scaleX: 1, ease: "power2.out" }, 0.35);

      // BEAT 3 — "CHEMISTRY" letterSpacing collapses from wide to tight
      gsap.set(b3word.current, { letterSpacing: "0.55em", opacity: 0, y: 35 });
      gsap.set(b3sub.current, { opacity: 0, y: 18 });
      gsap.set(b3circle.current, { opacity: 0, scale: 0.5 });
      gsap
        .timeline({
          scrollTrigger: {
            trigger: "#beat3",
            start: "top top",
            end: "+=80%",
            scrub: 0.75,
          },
        })
        .to(
          b3word.current,
          { letterSpacing: "0.04em", opacity: 1, y: 0, ease: "power2.out" },
          0,
        )
        .to(b3circle.current, { opacity: 1, scale: 1, ease: "power2.out" }, 0)
        .to(b3sub.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.4);

      // BEAT 4 — three lines wipe up with stagger
      gsap.set(b4top.current, { y: "120%", opacity: 0 });
      gsap.set(b4mid.current, { y: "120%", opacity: 0, x: "2.5%" });
      gsap.set(b4bot.current, { y: "120%", opacity: 0, x: "-2.5%" });
      gsap
        .timeline({
          scrollTrigger: {
            trigger: "#beat4",
            start: "top top",
            end: "+=80%",
            scrub: 0.6,
          },
        })
        .to(b4top.current, { y: 0, opacity: 1, ease: "power3.out" }, 0)
        .to(b4mid.current, { y: 0, opacity: 1, x: 0, ease: "power3.out" }, 0.1)
        .to(b4bot.current, { y: 0, opacity: 1, x: 0, ease: "power3.out" }, 0.2);

      // BEAT 5 — serif interlude breathes in, two lines sweep
      gsap.set(b5text.current, { opacity: 0, scale: 0.91, y: 28 });
      gsap.set(b5line1.current, { scaleX: 0 });
      gsap.set(b5line2.current, { scaleX: 0 });
      gsap
        .timeline({
          scrollTrigger: {
            trigger: "#beat5",
            start: "top top",
            end: "+=80%",
            scrub: 0.7,
          },
        })
        .to(b5line1.current, { scaleX: 1, ease: "power2.out" }, 0)
        .to(
          b5text.current,
          { opacity: 1, scale: 1, y: 0, ease: "power2.out" },
          0.1,
        )
        .to(b5line2.current, { scaleX: 1, ease: "power2.out" }, 0.45);

      // BEAT 6 — "NOW." zooms out of a blur into focus
      gsap.set(b6word.current, {
        scale: 2.8,
        opacity: 0,
        filter: "blur(50px)",
      });
      gsap.set(b6label.current, { opacity: 0, y: 14 });
      gsap.set(b6circle.current, { opacity: 0, scale: 0.15 });
      gsap
        .timeline({
          scrollTrigger: {
            trigger: "#beat6",
            start: "top top",
            end: "+=80%",
            scrub: 0.6,
          },
        })
        .to(
          b6word.current,
          { scale: 1, opacity: 1, filter: "blur(0px)", ease: "power3.out" },
          0,
        )
        .to(b6circle.current, { opacity: 1, scale: 1, ease: "power2.out" }, 0)
        .to(b6label.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.5);

      // BEAT 7 — final lines slam from sides
      gsap.set(b7line1.current, { x: "-100vw", opacity: 0 });
      gsap.set(b7line2.current, { x: "100vw", opacity: 0 });
      gsap.set(b7label.current, { opacity: 0, y: 18 });
      gsap
        .timeline({
          scrollTrigger: {
            trigger: "#beat7",
            start: "top top",
            end: "+=80%",
            scrub: 0.6,
          },
        })
        .to(b7line1.current, { x: 0, opacity: 1, ease: "power3.out" }, 0)
        .to(b7line2.current, { x: 0, opacity: 1, ease: "power3.out" }, 0.08)
        .to(b7label.current, { opacity: 1, y: 0, ease: "power2.out" }, 0.45);
    });

    return () => mm.revert();
  }, []);

  return (
    <>
      <GlobalStyle />
      <Wrapper>
        {/* BEAT 1 */}
        <Beat id="beat1" h="220vh">
          <BeatSticky bg="#06000d">
            <Grain />
            <AccentCircle
              ref={b1Circle}
              c="rgba(124,58,237,0.35)"
              s="600px"
              top="-10%"
              left="-10%"
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "1.5rem",
                zIndex: 2,
              }}
            >
              <GiantWord ref={b1Word} size="clamp(7rem, 22vw, 22rem)">
                FORGET
              </GiantWord>
              <SmallLabel ref={b1Label}>
                everything you know about dating
              </SmallLabel>
            </div>
            <Counter>
              <CountNum>01</CountNum>
              <CountSep>/</CountSep>
              <CountNum>07</CountNum>
            </Counter>
          </BeatSticky>
        </Beat>

        {/* BEAT 2 */}
        <Beat id="beat2" h="220vh">
          <BeatSticky bg="#08000f">
            <Grain />
            <AccentLine ref={b2line} c="rgba(232,121,249,0.5)" top="50%" />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "1.5rem",
                zIndex: 2,
              }}
            >
              <SplitLayout>
                <GiantWord
                  ref={b2top}
                  size="clamp(4.5rem, 15vw, 15rem)"
                  className="stroke"
                >
                  THE
                </GiantWord>
                <GiantWord
                  ref={b2bot}
                  size="clamp(3.8rem, 12vw, 12rem)"
                  className="gradient"
                >
                  ALGORITHM
                </GiantWord>
              </SplitLayout>
              <SmallLabel c="rgba(255,255,255,0.7)">
                can't feel what you feel
              </SmallLabel>
            </div>
            <Counter>
              <CountNum>02</CountNum>
              <CountSep>/</CountSep>
              <CountNum>07</CountNum>
            </Counter>
          </BeatSticky>
        </Beat>

        {/* BEAT 3 */}
        <Beat id="beat3" h="220vh">
          <BeatSticky bg="#0a0012">
            <Grain />
            <AccentCircle
              ref={b3circle}
              c="rgba(232,121,249,0.22)"
              s="700px"
              top="20%"
              right="-15%"
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "1.5rem",
                zIndex: 2,
              }}
            >
              <GiantWord
                ref={b3word}
                size="clamp(4rem, 14vw, 14rem)"
                className="gradient"
              >
                CHEMISTRY
              </GiantWord>
              <SmallLabel ref={b3sub} c="#e879f9">
                is instant — or it isn't
              </SmallLabel>
            </div>
            <Counter>
              <CountNum>03</CountNum>
              <CountSep>/</CountSep>
              <CountNum>07</CountNum>
            </Counter>
          </BeatSticky>
        </Beat>

        {/* BEAT 4 */}
        <Beat id="beat4" h="240vh">
          <BeatSticky bg="#070010">
            <Grain />
            <SplitLayout align="flex-end" style={{ paddingRight: "6vw" }}>
              <GiantWord
                ref={b4top}
                size="clamp(5rem, 17vw, 17rem)"
                className="stroke"
              >
                YOU
              </GiantWord>
              <GiantWord ref={b4mid} size="clamp(5rem, 17vw, 17rem)">
                SEE
              </GiantWord>
              <GiantWord
                ref={b4bot}
                size="clamp(5rem, 17vw, 17rem)"
                className="gradient"
              >
                HER.
              </GiantWord>
            </SplitLayout>
            <Counter>
              <CountNum>04</CountNum>
              <CountSep>/</CountSep>
              <CountNum>07</CountNum>
            </Counter>
          </BeatSticky>
        </Beat>

        {/* BEAT 5 */}
        <Beat id="beat5" h="220vh">
          <BeatSticky bg="#05000b">
            <Grain />
            <AccentLine
              ref={b5line1}
              c="rgba(168,85,247,0.4)"
              top="calc(50% - 90px)"
            />
            <AccentLine
              ref={b5line2}
              c="rgba(168,85,247,0.4)"
              top="calc(50% + 90px)"
              orig="right"
            />
            <Interlude ref={b5text}>
              Before the thought,
              <br />
              before the words —<br />
              <span style={{ color: "#e879f9", fontStyle: "italic" }}>
                you already know.
              </span>
            </Interlude>
            <Counter>
              <CountNum>05</CountNum>
              <CountSep>/</CountSep>
              <CountNum>07</CountNum>
            </Counter>
          </BeatSticky>
        </Beat>

        {/* BEAT 6 */}
        <Beat id="beat6" h="220vh">
          <BeatSticky bg="#0c0018">
            <Grain />
            <AccentCircle
              ref={b6circle}
              c="rgba(168,85,247,0.4)"
              s="800px"
              style={{
                top: "50%",
                left: "50%",
                transform: "translate(-50%,-50%)",
              }}
            />
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                gap: "1.2rem",
                zIndex: 2,
              }}
            >
              <GiantWord
                ref={b6word}
                size="clamp(8rem, 28vw, 28rem)"
                className="gradient"
              >
                NOW.
              </GiantWord>
              <SmallLabel ref={b6label} c="#c084fc">
                act on what your body already knows
              </SmallLabel>
            </div>
            <Counter>
              <CountNum>06</CountNum>
              <CountSep>/</CountSep>
              <CountNum>07</CountNum>
            </Counter>
          </BeatSticky>
        </Beat>

        {/* BEAT 7 */}
        <Beat id="beat7" h="240vh">
          <BeatSticky bg="#060010">
            <Grain />
            <SplitLayout align="flex-start" style={{ paddingLeft: "5vw" }}>
              <GiantWord
                ref={b7line1}
                size="clamp(4rem, 13vw, 13rem)"
                c="rgba(255,255,255,0.9)"
              >
                FIND YOUR
              </GiantWord>
              <GiantWord
                ref={b7line2}
                size="clamp(4rem, 13vw, 13rem)"
                className="gradient"
              >
                MATCH
                <Cursor />
              </GiantWord>
            </SplitLayout>
            <SmallLabel
              ref={b7label}
              style={{ position: "absolute", bottom: "3rem", right: "3rem" }}
              c="#a855f7"
            >
              download the app ↓
            </SmallLabel>
            <Counter>
              <CountNum>07</CountNum>
              <CountSep>/</CountSep>
              <CountNum>07</CountNum>
            </Counter>
          </BeatSticky>
        </Beat>
      </Wrapper>
    </>
  );
}
